import { useEffect, useMemo, useState } from "react";
import {
  Bar, CartesianGrid, ComposedChart, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { kellerNachRegion, ladeWeltkarte, weltkarteCache } from "../dienste/weltkarte.js";
import { baueStatistik } from "../kern/fachlogik.js";
import { fmtEuro, fmtZahl } from "../kern/format.js";
import { STAT_DIMENSIONEN } from "../kern/konstanten.js";
import { Auswahl, Leer, Seitenkopf, Werkzeugleiste } from "../ui/primitive.jsx";
import { FilterFelder, filtereDb, useWeinFilter } from "../ui/weinfilter.jsx";
import { t } from "../i18n/index.js";
import { landName } from "../i18n/laender.js";


/* Waagrechtes Balkendiagramm in Kellerbuch-Optik (ohne Fremd-Lib). */
/** Balkenliste einer Statistik-Dimension. Die Metrik („wert" oder
    „flaschen") bestimmt Sortierung, Balkenlänge und Prozentanteil;
    die jeweils andere Größe bleibt als Zusatzangabe sichtbar. */
function BalkenListe({ daten, farbe = "var(--granat)", metrik = "wert" }) {
  const istWert = metrik === "wert";
  const groesse = (d) => (istWert ? d.wert : d.flaschen);
  const reihen = useMemo(
    () => [...(daten || [])].sort((a, b) => groesse(b) - groesse(a)),
    [daten, metrik],
  );
  if (reihen.length === 0) return <p className="wl-statleer">{t("Noch keine Daten.")}</p>;
  const max = Math.max(...reihen.map(groesse), 1);
  const gesamt = reihen.reduce((s, d) => s + groesse(d), 0) || 1;
  const flTxt = (d) => `${fmtZahl(Math.round(d.flaschen))} Fl.`;
  return (
    <div className="wl-statbalken">
      {reihen.map((d) => (
        <div className="wl-statbalkenzeile" key={d.name}>
          <span className="wl-statbalkenname" title={d.name}>{d.name}</span>
          <span className="wl-statbalkenspur">
            <span className="wl-statbalkenfuell" style={{ width: `${(groesse(d) / max) * 100}%`, background: farbe }} />
          </span>
          <span className="wl-statbalkenwert num">
            {istWert ? fmtEuro(d.wert) : flTxt(d)}
            <span className="wl-statbalkenanteil"> · {istWert ? flTxt(d) : fmtEuro(d.wert)} · {Math.round((groesse(d) / gesamt) * 100)} %</span>
          </span>
        </div>
      ))}
    </div>
  );
}


/* Heatmap des Kellers auf der Weltkarte — Blobs je Weinregion.
   Quelle, Metrik und Jahr folgen dem Seiten-Umschalter. */
function WeltkarteHeatmap({ db, quelle, metrik, jahr }) {
  const [pfade, setPfade] = useState(weltkarteCache);
  const [fehler, setFehler] = useState(null);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    if (pfade) return;
    let ab = false;
    ladeWeltkarte().then((p) => { if (!ab) setPfade(p); }).catch((e) => { if (!ab) setFehler(e.message); });
    return () => { ab = true; };
  }, []);

  const daten = useMemo(() => kellerNachRegion(db, jahr), [db, jahr]);
  const istWert = metrik === "wert";
  /* Getrunken und Weinkarten lesen beide die Entnahmen — beim
     Weinkarten-Blick sind sie schon auf Kartenverkäufe eingegrenzt. */
  const ausEntnahmen = quelle === "getrunken" || quelle === "weinkarte";
  const feld = ausEntnahmen ? (istWert ? "trinkWert" : "trinkFl") : (istWert ? "bestandWert" : "bestandFl");
  const punkte = daten.map((d) => ({ region: d.region, wert: d[feld] })).filter((d) => d.wert > 0)
    .sort((a, b) => b.wert - a.wert);
  const max = punkte.reduce((m, d) => Math.max(m, d.wert), 0) || 1;
  const farbe = quelle === "bestand" ? "#722F37" : "#B9A15C";
  const fmtWert = (v) => istWert ? fmtEuro(v) : `${v} ${v === 1 ? "Flasche" : "Flaschen"}`;

  /* Sichtausschnitt: volle Breite, Pole beschnitten (Lat ~ +64 … −56). */
  const vb = { x: 0, y: 26, w: 360, h: 120 };

  return (
    <section className="wl-weltkarte">
      <div className="wl-abschnittskopf">
        <h2 className="wl-statblocktitel">
          {t("Weltkarte · {was}", {
            was: quelle === "getrunken" ? t("Getrunken")
              : quelle === "weinkarte" ? t("Aus Weinkarten") : t("Kellerbestand"),
          })}
        </h2>
      </div>

      {fehler ? (
        <p className="wl-statleer">Die Weltkarte konnte nicht geladen werden ({fehler}). Bitte Internetverbindung prüfen und Seite neu laden.</p>
      ) : !pfade ? (
        <p className="wl-statleer">{t("Weltkarte wird geladen …")}</p>
      ) : (
        <div className="wl-kartenrahmen" onMouseLeave={() => setHover(null)}>
          <svg viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} className="wl-weltsvg" role="img" aria-label={t("Weltkarte des Kellers")}>
            <defs>
              <filter id="wl-heatblur" x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="1.4" />
              </filter>
            </defs>
            <rect x={vb.x} y={vb.y} width={vb.w} height={vb.h} fill="#EEF1F0" />
            <g>
              {pfade.map((d, i) => <path key={i} d={d} fill="#E4DFD1" stroke="#CFC8B6" strokeWidth={0.15} />)}
            </g>
            <g filter="url(#wl-heatblur)">
              {punkte.map((d) => {
                const r = 1.6 + 9 * Math.sqrt(d.wert / max);
                const op = 0.32 + 0.4 * (d.wert / max);
                return <circle key={d.region.id} cx={d.region.lon + 180} cy={90 - d.region.lat} r={r} fill={farbe} opacity={op} />;
              })}
            </g>
            <g>
              {punkte.map((d) => (
                <circle key={d.region.id} cx={d.region.lon + 180} cy={90 - d.region.lat} r={1} fill={farbe}
                  stroke="#FBFAF6" strokeWidth={0.3} style={{ cursor: "pointer" }}
                  onMouseEnter={() => setHover(d)} onMouseMove={() => setHover(d)} onMouseLeave={() => setHover(null)}>
                  <title>{`${d.region.name}${d.region.land ? " · " + landName(d.region.land) : ""}: ${fmtWert(d.wert)}`}</title>
                </circle>
              ))}
            </g>
          </svg>
          {hover && (
            <div className="wl-kartentooltip">
              <strong>{hover.region.name}</strong>{hover.region.land ? ` · ${landName(hover.region.land)}` : ""}
              <span className="num">{fmtWert(hover.wert)}</span>
            </div>
          )}
          {punkte.length === 0 && (
            <div className="wl-karteleer">{t("Keine Positionen mit Koordinaten für diese Auswahl.")}</div>
          )}
        </div>
      )}
    </section>
  );
}


function StatistikAnsicht({ db: db0 }) {
  const [quelle, setQuelle] = useState("bestand"); /* bestand | getrunken | weinkarte */
  const [karteFilter, setKarteFilter] = useState("");   /* "" = alle Weinkarten */
  const [metrik, setMetrik] = useState("flaschen"); /* flaschen | wert | umsatz | gewinn */
  const [jahr, setJahr] = useState(""); /* "" = alle Kalenderjahre */
  const nachWert = metrik !== "flaschen";
  const jahrZahl = jahr === "" ? null : parseInt(jahr, 10);

  /* Dieselbe Filterung wie im Keller, eigener gespeicherter Zustand.
     Gefiltert wird einmal das Kellerbuch — Kennzahlen, Balken,
     Weltkarte und Verlauf folgen dadurch automatisch. */
  const filter = useWeinFilter(db0, "statistik");
  const db = useMemo(() => filtereDb(db0, filter), [db0, filter.signatur]);

  /* Welche Weinkarten kommen in den Entnahmen überhaupt vor? */
  const karten = useMemo(() => {
    const nach = new Map();
    for (const e of db0.entnahmen || []) {
      if (!e.karteId) continue;
      if (!nach.has(e.karteId)) nach.set(e.karteId, e.karteTitel || "Weinkarte");
    }
    return [...nach.entries()].map(([id, titel]) => ({ id, titel }))
      .sort((a, b) => a.titel.localeCompare(b.titel, "de"));
  }, [db0.entnahmen]);

  /* Für die Weinkarten-Auswertung zählen nur Entnahmen, die einer
     Karte zugeordnet sind — wahlweise eingegrenzt auf eine davon.

     Der Trick für Einkauf, Umsatz und Gewinn: Die übrigen Auswertungen
     rechnen alle „Menge × preis". Also wird preis je Entnahme auf die
     gewünschte Größe gesetzt — damit stimmen Kennzahlen, Balken,
     Weltkarte und Verlauf ohne Sonderbehandlung. */
  const dbQuelle = useMemo(() => {
    if (quelle !== "weinkarte") return db;
    const entnahmen = (db.entnahmen || [])
      .filter((e) => e.karteId && (!karteFilter || e.karteId === karteFilter))
      .map((e) => {
        const ek = e.preis != null ? e.preis : 0;
        const vk = e.kartePreis != null ? e.kartePreis : 0;
        const preis = metrik === "umsatz" ? vk
          : metrik === "gewinn" ? vk - ek
            : ek;
        return { ...e, preis, einkauf: ek, verkauf: vk };
      });

    /* Nur Weine, die auf einer Karte stehen oder von ihr ausgeschenkt
       wurden — Ein- und Auslagerungen beziehen sich dann ebenfalls
       ausschließlich auf diese Weine. */
    const weinIds = new Set(entnahmen.map((e) => e.weinId));
    for (const k of db.weinkarten || []) {
      if (karteFilter && k.id !== karteFilter) continue;
      for (const e of k.eintraege || []) weinIds.add(e.weinId);
    }
    /* Auch im Verlauf soll der Abgang dieselbe Größe zeigen wie die
       Kennzahl — sonst stünde neben „Verkauf" ein Einkaufswert. */
    const nachId = new Map(entnahmen.map((e) => [e.id, e]));
    const bewegungen = (db.bewegungen || [])
      .filter((b) => weinIds.has(b.weinId) && (b.art === "zugang" || b.karteId))
      .map((b) => {
        const e = b.art === "entnahme" ? nachId.get(b.id) : null;
        return e ? { ...b, preis: e.preis } : b;
      });

    return { ...db, entnahmen, bewegungen };
  }, [db, quelle, karteFilter, metrik]);

  const stat = useMemo(() => baueStatistik(dbQuelle, jahrZahl), [dbQuelle, jahrZahl]);

  /* Einkauf, Umsatz, Gewinn und Spanne — die Kennzahlen einer Karte. */
  const kartenZahlen = useMemo(() => {
    if (quelle !== "weinkarte") return null;
    let flaschen = 0, einkauf = 0, umsatz = 0, ohnePreis = 0;
    for (const e of dbQuelle.entnahmen || []) {
      if (jahrZahl != null && new Date(e.datum).getFullYear() !== jahrZahl) continue;
      const m = e.menge || 0;
      flaschen += m;
      einkauf += (e.einkauf || 0) * m;
      umsatz += (e.verkauf || 0) * m;
      if (e.kartePreis == null) ohnePreis += m;
    }
    const gewinn = umsatz - einkauf;
    const spanne = umsatz > 0 ? (gewinn / umsatz) * 100 : null;
    return { flaschen, einkauf, umsatz, gewinn, spanne, ohnePreis };
  }, [dbQuelle, quelle, jahrZahl]);
  const aktiv = quelle === "weinkarte" ? stat.getrunken : stat[quelle];
  const flaschenGesamt = aktiv.flaschen;

  /* Auswählbar sind die Jahre, in denen tatsächlich ein- oder
     ausgelagert wurde — aus dem Bewegungsprotokoll. Das gerade
     gewählte Jahr bleibt in der Liste, auch wenn die Filterung es
     leer räumt; sonst stünde die Auswahl auf einem fehlenden Eintrag. */
  const jahre = useMemo(() => {
    const set = new Set(stat.bewegungen.map((b) => b.jahr));
    if (jahrZahl != null) set.add(jahrZahl);
    return [...set].sort((a, b) => b - a);
  }, [stat.bewegungen, jahrZahl]);

  const imJahr = jahrZahl != null;
  const kopf = quelle === "bestand"
    ? [[imJahr ? `Eingelagert ${jahrZahl}` : "Flaschen im Keller", flaschenGesamt],
       ["Verschiedene Weine", aktiv.weine],
       [imJahr ? "Wert eingelagert" : "Kellerwert", fmtEuro(aktiv.wert)]]
    : [[imJahr ? `Getrunken ${jahrZahl}` : "Getrunkene Flaschen", flaschenGesamt],
       ["Verschiedene Weine", aktiv.weine],
       ["Wert getrunken", fmtEuro(aktiv.wert)]];

  const farbe = quelle === "bestand" ? "var(--granat)" : "var(--nebel)";

  return (
    <>
      <Seitenkopf
        titel="Statistik"
        unterzeile={`${quelle === "bestand"
          ? (imJahr ? `Im Jahr ${jahrZahl} eingelagerte Flaschen` : "Verteilung des aktuellen Kellerbestands")
          : (imJahr ? `Im Jahr ${jahrZahl} getrunkene Flaschen` : "Verteilung aller getrunkenen Flaschen")} nach ${nachWert ? "Wert" : "Flaschenzahl"}`}
      />

      <Werkzeugleiste
        suche={filter.suche} setSuche={filter.setSuche} sucheLabel={t("Weine durchsuchen …")}
        filterAktiv={filter.aktiv}
        onReset={filter.reset}
        filter={<FilterFelder db={db0} f={filter} />}
      />

      <div className="wl-statumschalter" role="tablist">
        <button role="tab" aria-selected={quelle === "bestand"}
          className={quelle === "bestand" ? "aktiv" : ""} onClick={() => setQuelle("bestand")}>{imJahr ? "Eingelagert" : "Kellerbestand"}</button>
        <button role="tab" aria-selected={quelle === "getrunken"}
          className={quelle === "getrunken" ? "aktiv" : ""} onClick={() => setQuelle("getrunken")}>{t("Getrunken")}</button>
        <button role="tab" aria-selected={quelle === "weinkarte"}
          className={quelle === "weinkarte" ? "aktiv" : ""} onClick={() => setQuelle("weinkarte")}>{t("Aus Weinkarten")}</button>
      </div>

      {quelle === "weinkarte" && (
        <div className="wl-kartenauswertung">
          <Auswahl value={karteFilter} onChange={(e) => setKarteFilter(e.target.value)}
            aria-label={t("Nach Weinkarte filtern")}>
            <option value="">{t("Alle Weinkarten")}</option>
            {karten.map((k) => <option key={k.id} value={k.id}>{k.titel}</option>)}
          </Auswahl>
          {kartenZahlen && (
            <div className="wl-kartenzahlen">
              <span><em>{kartenZahlen.flaschen}</em> {kartenZahlen.flaschen === 1 ? "Flasche" : "Flaschen"}</span>
              <span><em>{fmtEuro(kartenZahlen.einkauf)}</em>{t("Einkauf")}</span>
              <span><em>{fmtEuro(kartenZahlen.umsatz)}</em>{t("Verkauf")}</span>
              <span className={kartenZahlen.gewinn < 0 ? "ist-verlust" : "ist-gewinn"}>
                <em>{fmtEuro(kartenZahlen.gewinn)}</em> {t("Gewinn")}
                {kartenZahlen.spanne != null ? ` · ${Math.round(kartenZahlen.spanne)} % Spanne` : ""}
              </span>
              {kartenZahlen.ohnePreis > 0 && (
                <span className="ist-hinweis">{kartenZahlen.ohnePreis} Fl. ohne Kartenpreis</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ein Umschalter für die ganze Seite: Karte, Balken und Verlauf. */}
      <div className="wl-statsteuerung">
        <span className="wl-segment" role="group" aria-label={t("Auswertung nach Flaschen oder Wert")}>
          <button className={"wl-segbtn" + (metrik === "flaschen" ? " ist-aktiv" : "")}
            onClick={() => setMetrik("flaschen")}>{t("Flaschen")}</button>
          {quelle === "weinkarte" ? (
            <>
              <button className={"wl-segbtn" + (metrik === "wert" ? " ist-aktiv" : "")}
                onClick={() => setMetrik("wert")}>{t("Einkauf")}</button>
              <button className={"wl-segbtn" + (metrik === "umsatz" ? " ist-aktiv" : "")}
                onClick={() => setMetrik("umsatz")}>{t("Verkauf")}</button>
              <button className={"wl-segbtn" + (metrik === "gewinn" ? " ist-aktiv" : "")}
                onClick={() => setMetrik("gewinn")}>{t("Gewinn")}</button>
            </>
          ) : (
            <button className={"wl-segbtn" + (nachWert ? " ist-aktiv" : "")}
              onClick={() => setMetrik("wert")}>{t("Wert")}</button>
          )}
        </span>
        <Auswahl className="wl-jahrfilter" value={jahr} onChange={(e) => setJahr(e.target.value)} aria-label={t("Nach Kalenderjahr der Bewegungen filtern")}>
          <option value="">{t("Alle Kalenderjahre")}</option>
          {jahre.map((j) => <option key={j} value={j}>{j}</option>)}
        </Auswahl>
      </div>

      <div className="wl-ledger">
        {kopf.map(([label, wert]) => (
          <div key={label}>
            <span className="wl-kennzahl">{typeof wert === "number" ? wert : wert}</span>
            <span className="wl-kennname">{label}</span>
          </div>
        ))}
      </div>

      {flaschenGesamt === 0 ? (
        <Leer text={filter.aktiv || filter.suche.trim()
          ? "Zu dieser Filterung gibt es keine Daten. Setze die Filter zurück oder wähle andere Werte."
          : (imJahr
            ? `Im Jahr ${jahrZahl} wurde ${quelle === "bestand" ? "nichts eingelagert" : "nichts getrunken"}. Wähle ein anderes Jahr.`
            : (quelle === "bestand"
              ? "Noch kein Bestand erfasst — lege Weine an und buche Zugänge."
              : "Noch keine Entnahmen protokolliert. Sobald du Flaschen entnimmst, erscheint hier ihre Verteilung."))} />
      ) : (
        <>
          <WeltkarteHeatmap db={dbQuelle} quelle={quelle} metrik={metrik} jahr={jahrZahl} />
          <div className="wl-statgitter">
            {STAT_DIMENSIONEN.map(([key, titel]) => (
              <section className="wl-statblock" key={key}>
                <h2 className="wl-statblocktitel">{t(titel)}</h2>
                <BalkenListe daten={aktiv.dim[key]} farbe={farbe} metrik={metrik} />
              </section>
            ))}
          </div>
        </>
      )}

      <BewegungsChart daten={stat.bewegungen} metrik={metrik} />
    </>
  );
}


/* Ein-/Auslagerungen je Kalenderjahr als gruppiertes Balkendiagramm. */
function BewegungsChart({ daten, metrik }) {
  if (!daten || daten.length === 0) return null;
  const istWert = metrik === "wert";
  const einKey = istWert ? "einWert" : "ein";
  const ausKey = istWert ? "ausWert" : "aus";
  return (
    <section className="wl-statbewegung">
      <div className="wl-abschnittskopf">
        <h2 className="wl-statblocktitel">{t("Ein- & Auslagerungen nach Jahren")}</h2>
      </div>
      <div className="wl-diagramm">
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={daten} margin={{ top: 8, right: 4, bottom: 0, left: -12 }}>
            <CartesianGrid stroke="#ECE8DD" vertical={false} />
            <XAxis dataKey="jahr" tick={{ fontSize: 11, fill: "#6F6A5E" }} tickLine={false} axisLine={{ stroke: "#D8D3C4" }} />
            <YAxis tick={{ fontSize: 11, fill: "#6F6A5E" }} tickLine={false} axisLine={false} allowDecimals={false}
              tickFormatter={(v) => istWert ? `${Math.round(v)} €` : v} width={istWert ? 52 : 32} />
            <Tooltip
              contentStyle={{ fontFamily: "var(--sans)", fontSize: 13, border: "1px solid #A9A392", borderRadius: 0, padding: "8px 10px" }}
              labelStyle={{ color: "#1B1914", fontWeight: 600 }}
              formatter={(wert, name) => [istWert ? fmtEuro(wert) : `${wert} Flaschen`, name]}
            />
            <Legend wrapperStyle={{ fontSize: 11, fontFamily: "var(--sans)" }} />
            <Bar dataKey={einKey} name="Eingelagert" fill="#722F37" barSize={16} radius={[1, 1, 0, 0]} />
            <Bar dataKey={ausKey} name="Ausgelagert" fill="#B9A15C" barSize={16} radius={[1, 1, 0, 0]} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <p className="wl-statleer">{t("Aus dem Bewegungsprotokoll ab Aufzeichnungsbeginn — frühere Ein-/Auslagerungen sind nicht rekonstruierbar. Der Wert basiert auf dem hinterlegten Jahrgangspreis.")}</p>
    </section>
  );
}

export { BalkenListe, BewegungsChart, StatistikAnsicht, WeltkarteHeatmap };
